/**
 * projectStore.js — the central Zustand store for LiTeCAB (Phase 2 "central
 * store" refactor).
 *
 * This holds everything that used to live as ~20 `useState` slices + a wall of
 * `useCallback` handlers inside App.js. App.js is now a thin orchestrator: it
 * injects the modal helpers (`setModal`), wires the Electron menu / web-hotkey
 * IPC events to these actions, registers the agent bridge against
 * `useProjectStore.getState()`, and renders. The Workspace/* components still
 * read a `WorkspaceContext` that App builds from this store, so they were left
 * untouched.
 *
 * Anything that needs React context (the modal alert/confirm helpers) is
 * injected via `setModal()`. Everything else the actions need — `ipc`, the pure
 * calculators, XLSX export — is a plain module import and lives right here, so
 * the agent bridge can call store actions directly without going through React.
 */

import { create } from 'zustand';
import * as XLSX from 'xlsx';
import ipc from '../services/ipc';
import { isTemplateMode, runTemplateLCA } from '../services/lcaCalculator';
import { runTemplateTEA } from '../services/teaCalculator';
import { runCircularity } from '../services/circularityCalculator';
import { resolveAllSubInventories } from '../services/subInventoryResolver';
import { findBuiltinTemplate, isBuiltinTemplateFile } from '../services/templateCatalog';
import { buildProjectFromTemplate } from '../services/templateProject';
import { newProjectId, freeProjectName } from '../services/projectIdentity';

const emptyResults = () => ({ lca: null, tea: null });

// React-setState parity: accept either a next value or an updater(prev).
const apply = (value, prev) => (typeof value === 'function' ? value(prev) : value);

const sanitizeFileName = (name) =>
  name.replace(/[/\\:*?"<>|]/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').toLowerCase();

function buildTemplateData(project, templateId) {
  return {
    project_name: project?.project_info?.name || project?.project_name || templateId,
    template_id: templateId,
    description: project?.project_info?.description || '',
    version: '1.0',
    created_date: new Date().toISOString().split('T')[0],
    data_source: 'User-customized template',
    impact_method: 'TRACI v2.1',
    functional_unit: project?.functional_unit || {},
    traci_categories: project?.traci_categories || {},
    // Keep the dataset the rows are scored on (GREET for BatPaC projects).
    ...(project?.impact_dataset ? { impact_dataset: project.impact_dataset } : {}),
    ...(project?.batpac ? { batpac: project.batpac } : {}),
    ...(project?.design_parameters ? { design_parameters: project.design_parameters } : {}),
    ...(project?.coating_parameters ? { coating_parameters: project.coating_parameters } : {}),
    processes: project?.processes,
  };
}

export const useProjectStore = create((set, get) => {
  // -- internal helpers (closed over set/get; not part of public state) --------
  const setStatus = (statusMessage) => set({ statusMessage });
  const modal = () => get()._modal;

  async function autoSave(payload, successMsg, failMsg) {
    try {
      setStatus('Saving…');
      const result = await ipc.invoke('auto-save-project', payload);
      if (result?.success) setStatus(successMsg);
      else throw new Error(result?.error || 'Save returned failure');
    } catch (err) {
      console.error('[store] Auto-save failed:', err);
      setStatus(failMsg);
    }
  }

  // Next free process id (P001, P002, …) based on the highest existing number.
  function nextProcessId(project) {
    let maxNum = 0;
    Object.keys(project.processes).forEach((id) => {
      const m = id.match(/(\d+)/);
      if (m) maxNum = Math.max(maxNum, parseInt(m[1], 10));
    });
    return `P${String(maxNum + 1).padStart(3, '0')}`;
  }

  // A blank process, inheriting type/category from a neighbour (`basis`) so a new
  // step drops into the flow with sensible defaults.
  function blankProcess(project, basis, id) {
    return {
      id,
      name: 'New_Process',
      process_type: basis?.process_type || 'Processing',
      manufacturing_category: basis?.manufacturing_category || project.project_info?.category?.toLowerCase() || 'default',
      hierarchy_level: basis?.hierarchy_level || 0,
      is_side_input: false,
      equipment: '',
      parameters: { duration_hours: 0, temperature_c: 25 },
      next_process_ids: [],
      description: '',
      products: [],
      lci_inputs: [],
    };
  }

  return {
    // ---- state ---------------------------------------------------------------
    project: null,
    selectedProcess: null,
    analysisResults: emptyResults(),
    isAnalyzing: false,
    statusMessage: 'Ready',
    showStartup: true,
    workspaceMode: 'build', // 'build' | 'inventory' | 'results' | 'compare'
    leftViewMode: 'diagram', // 'diagram' | 'list' | 'table' (Build sub-view)
    // True when the project was edited since the last analysis run (drives the
    // "results out of date" coach card).
    dirty: false,
    traciDbMode: 'original', // 'original' | 'noisy'
    isDemoBuild: false,
    // Output-price inline editor: "<processId>::<outputIndex>" of the active row.
    editingOutputPrice: null,
    // Inline process-name editor in the inspector header.
    editingSpecName: false,
    specNameDraft: '',
    // Undo stack for copilot-applied edits ({ project, analysisResults, label }).
    agentHistory: [],
    showTemplateSaveDialog: false,
    newTemplateName: '',
    // Modal helpers injected by App once it has useModal(); safe no-ops until then.
    _modal: { showAlert: async () => {}, showConfirm: async () => false },

    // ---- plain setters (updater-aware, React-setState parity) ----------------
    setModal: (m) => set({ _modal: m }),
    setProject: (v) => set((s) => ({ project: apply(v, s.project) })),
    setSelectedProcess: (v) => set((s) => ({ selectedProcess: apply(v, s.selectedProcess) })),
    setAnalysisResults: (v) => set((s) => ({ analysisResults: apply(v, s.analysisResults) })),
    setLeftViewMode: (v) => set({ leftViewMode: v }),
    setTraciDbMode: (v) => set({ traciDbMode: v }),
    setStatusMessage: (v) => set({ statusMessage: v }),
    setEditingOutputPrice: (v) => set({ editingOutputPrice: v }),
    setEditingSpecName: (v) => set((s) => ({ editingSpecName: apply(v, s.editingSpecName) })),
    setSpecNameDraft: (v) => set({ specNameDraft: v }),
    setShowTemplateSaveDialog: (v) => set({ showTemplateSaveDialog: v }),
    setNewTemplateName: (v) => set({ newTemplateName: v }),
    setAgentHistory: (v) => set((s) => ({ agentHistory: apply(v, s.agentHistory) })),

    // ---- build info ----------------------------------------------------------
    // Hide licensed-only UI in demo builds; default to non-demo if IPC fails.
    initBuildInfo: async () => {
      try {
        const res = await ipc.invoke('get-build-info');
        if (res?.isDemoBuild) set({ isDemoBuild: true, traciDbMode: 'noisy' });
      } catch { /* assume non-demo */ }
    },

    // ---- navigation ----------------------------------------------------------
    // Switch focus mode. Results/Compare are project-wide, so deselect the
    // process (Build's inspector shouldn't linger over a full-project view).
    goToMode: (mode) => {
      if (!mode) return;
      if (mode === 'results' || mode === 'compare') set({ workspaceMode: mode, selectedProcess: null });
      else set({ workspaceMode: mode });
    },
    // Selecting a process (diagram/table/list, or the copilot) opens it in Build.
    selectProcess: (proc) => {
      if (proc) set({ selectedProcess: proc, workspaceMode: 'build' });
      else set({ selectedProcess: null });
    },
    // Map the new focus mode directly, or translate the legacy left/right panel
    // semantics (used by the walkthrough) onto a workspace mode.
    setView: ({ left, right, mode } = {}) => {
      if (left) set({ leftViewMode: left });
      const resolved = mode
        || (right === 'analysis' ? 'results'
          : right === 'inventory' ? 'inventory'
            : right === 'compare' ? 'compare'
              : null);
      if (resolved) get().goToMode(resolved);
      return { leftViewMode: left || get().leftViewMode, workspaceMode: resolved || get().workspaceMode };
    },

    // ---- project lifecycle ---------------------------------------------------
    projectSelected: (input, source) => {
      const { showAlert } = modal();
      if (!input?.project_info?.name) {
        showAlert('Invalid project data.', { title: 'Error', type: 'error' });
        return;
      }
      if (!input.processes || Object.keys(input.processes).length === 0) {
        showAlert('Project has no processes.', { title: 'Error', type: 'error' });
        return;
      }
      // The id is what auto-save stores the project under (projectIdentity.js).
      // A reopened saved project keeps its id, so it saves in place; a new one
      // gets a fresh id unless its creator fixed one. A file opened from disk
      // always gets a fresh id: an old download must not save over the copy
      // kept in this browser.
      const keepId = input.project_id && source !== 'existing';
      const data = keepId ? input : { ...input, project_id: newProjectId() };
      set({
        project: data,
        analysisResults: data.analysis_results || emptyResults(),
        selectedProcess: null,
        showStartup: false,
        dirty: false,
        workspaceMode: 'build', // always open a freshly loaded project in Build
      });
      if (source === 'template') {
        autoSave(
          { ...data, analysis_results: data.analysis_results || emptyResults() },
          `Created and saved: ${data.project_info.name}`,
          `Created: ${data.project_info.name} (not saved)`,
        );
      } else {
        setStatus(`Loaded: ${data.project_info.name}`);
      }
    },

    newProject: async () => {
      const { project, analysisResults } = get();
      if (project) {
        const save = await modal().showConfirm(
          'Do you want to save the current project before creating a new one?',
          { title: 'Save Project?', type: 'warning' },
        );
        if (save) {
          try { await autoSave({ ...project, analysis_results: analysisResults }, 'Project saved', 'Save failed'); }
          catch (_) { /* continue even if save fails */ }
        }
      }
      set({ showStartup: true, project: null, selectedProcess: null, analysisResults: emptyResults(), statusMessage: 'Ready' });
    },

    openProject: async () => {
      const { project, analysisResults } = get();
      if (project) {
        const save = await modal().showConfirm(
          'Do you want to save the current project before opening another?',
          { title: 'Save Project?', type: 'warning' },
        );
        if (save) {
          try { await autoSave({ ...project, analysis_results: analysisResults }, 'Project saved', 'Save failed'); }
          catch (_) { /* continue even if save fails */ }
        }
      }
      try {
        const result = await ipc.invoke('show-open-dialog');
        if (!result.canceled && result.filePaths.length > 0) {
          setStatus('Loading project…');
          const data = await ipc.invoke('load-project', result.filePaths[0]);
          get().projectSelected(data, 'existing');
        }
      } catch (err) {
        console.error('[store] Open project failed:', err);
        setStatus('Failed to open project');
      }
    },

    // Web-only: load a project directly from an IDB filePath like
    // "idb://projects/<safe>".
    loadFromIdb: async (filePath) => {
      const { project } = get();
      const { showConfirm, showAlert } = modal();
      if (project) {
        const save = await showConfirm(
          'Do you want to save the current project before opening another?',
          { title: 'Open Saved Project', confirmText: 'Save & Open', cancelText: 'Discard & Open' },
        );
        if (save) await get().saveProject();
      }
      try {
        setStatus('Opening…');
        const data = await ipc.invoke('load-project', filePath);
        get().projectSelected(data, 'saved');
        setStatus(`Opened: ${data?.project_info?.name || filePath}`);
      } catch (err) {
        console.error('[store] load-from-idb failed:', err);
        setStatus('Failed to open project');
        showAlert(`Open failed:\n${err.message}`, { title: 'Open Error', type: 'error' });
      }
    },

    // Build a fresh project from the current shipped template, for a project
    // made from an older one (see templateVersion.js). It is saved as a new
    // project; the outdated one is left exactly as it is.
    recreateFromTemplate: async (file) => {
      const { project } = get();
      const { showConfirm, showAlert } = modal();
      const entry = findBuiltinTemplate(file);
      if (!project || !entry) return null;
      const oldName = project.project_info?.name || 'this project';
      const name = await freeProjectName(`${oldName} (updated)`);
      const ok = await showConfirm(
        `Create a new project, "${name}", from the current ${entry.name} template?\n\n"${oldName}" stays saved as it is. Changes you made to it are not copied over.`,
        { title: 'Re-create from Current Template', type: 'info', confirmText: 'Re-create', cancelText: 'Cancel' },
      );
      if (!ok) return null;
      try {
        setStatus('Loading current template…');
        const template = await ipc.invoke('load-template', entry.template, { builtin: true });
        const fresh = buildProjectFromTemplate(template, {
          name, file: entry.template, chemistry: entry.chemistry, category: entry.category,
        });
        get().projectSelected(fresh, 'template');
        return { name };
      } catch (err) {
        console.error('[store] Re-create from template failed:', err);
        setStatus('Re-create failed');
        showAlert(`Could not re-create the project:\n${err.message}`, { title: 'Error', type: 'error' });
        return null;
      }
    },

    saveProject: async () => {
      const { project, analysisResults } = get();
      const { showAlert } = modal();
      if (!project?.project_info) {
        showAlert('No project loaded.', { title: 'No Project', type: 'info' });
        return;
      }
      try {
        setStatus('Saving…');
        const payload = { ...project, analysis_results: analysisResults };
        const result = await ipc.invoke('save-project-as', payload);
        if (result?.canceled) setStatus('Save canceled');
        else if (result?.success) setStatus(`Saved: ${result.filePath}`);
        else throw new Error(result?.error || 'Save failed');
      } catch (err) {
        console.error('[store] Save failed:', err);
        setStatus('Failed to save project');
        showAlert(`Save failed:\n${err.message}`, { title: 'Save Error', type: 'error' });
      }
    },

    // ---- process editing -----------------------------------------------------
    updateProcess: async (processId, updates) => {
      const { project, selectedProcess, analysisResults } = get();
      if (!project) return;

      const { lci_inputs, products, output_materials, ...paramUpdates } = updates;
      const updatedProc = { ...project.processes[processId] };
      if (lci_inputs !== undefined) updatedProc.lci_inputs = lci_inputs;
      if (products !== undefined) updatedProc.products = products;
      if (output_materials !== undefined) updatedProc.output_materials = output_materials;
      if (Object.keys(paramUpdates).length > 0) {
        updatedProc.parameters = { ...updatedProc.parameters, ...paramUpdates };
      }

      const updatedProject = { ...project, processes: { ...project.processes, [processId]: updatedProc } };
      set({
        project: updatedProject,
        dirty: true,
        selectedProcess: selectedProcess?.id === processId ? updatedProc : selectedProcess,
      });
      await autoSave(
        { ...updatedProject, analysis_results: analysisResults },
        'Process updated & saved',
        'Process updated (save failed)',
      );
    },

    // Whole-project update: Table view edits (add/delete/edit processes) and
    // Results edits (design Apply, dataset, functional unit). Marks results stale.
    updateProjectTable: async (updatedProject, label = 'Table updated') => {
      const { analysisResults } = get();
      set({ project: updatedProject, dirty: true });
      await autoSave(
        { ...updatedProject, analysis_results: analysisResults },
        `${label} & saved`,
        `${label} (save failed)`,
      );
    },

    // Add a new step with explicit placement. This is the ONE add path — the
    // Build "Add step" dialog and the copilot's add_process tool both call it,
    // so a step always lands in the flow the same way.
    //   { afterId }            → appended after that step (afterId → new)
    //   { afterId, beforeId }   → inserted between them (afterId → new → beforeId)
    //   { beforeId }            → feeds into that step (new → beforeId)
    //   {}                      → a standalone step
    // Optional name / process_type / parameters / description seed the new step.
    addProcess: async ({ afterId = null, beforeId = null, name = null, process_type = null, parameters = null, description = null } = {}) => {
      const { project } = get();
      if (!project) return null;
      const newId = nextProcessId(project);
      const after = afterId && project.processes[afterId] ? project.processes[afterId] : null;
      const before = beforeId && project.processes[beforeId] ? beforeId : null;
      const processArr = Object.values(project.processes).sort((a, b) =>
        (a.id || '').localeCompare(b.id || '', undefined, { numeric: true }));
      const basis = after || processArr[processArr.length - 1];

      const newProc = blankProcess(project, basis, newId);
      if (name) newProc.name = String(name).trim().replace(/\s+/g, '_') || newProc.name;
      if (process_type) newProc.process_type = process_type;
      if (parameters && typeof parameters === 'object') newProc.parameters = { ...newProc.parameters, ...parameters };
      if (description != null) newProc.description = description;
      if (before) newProc.next_process_ids = [before];

      const processes = { ...project.processes, [newId]: newProc };
      if (after) {
        const next = [...(after.next_process_ids || [])];
        // If we're inserting between after → before, replace that one link;
        // otherwise just chain the new step on the end of after.
        if (before && next.includes(before)) {
          processes[afterId] = { ...after, next_process_ids: next.map((id) => (id === before ? newId : id)) };
        } else {
          processes[afterId] = { ...after, next_process_ids: [...next, newId] };
        }
      }
      await get().updateProjectTable({ ...project, processes });
      set({ selectedProcess: newProc, workspaceMode: 'build' });
      return { id: newId, name: newProc.name };
    },

    // Patch top-level process fields (type / category / equipment / description).
    // Parameters (duration, temperature) go through updateProcess instead.
    updateProcessFields: async (processId, fields) => {
      const { project, selectedProcess, analysisResults } = get();
      if (!project || !project.processes[processId]) return;
      const updatedProc = { ...project.processes[processId], ...fields };
      const updatedProject = { ...project, processes: { ...project.processes, [processId]: updatedProc } };
      set({
        project: updatedProject,
        dirty: true,
        selectedProcess: selectedProcess?.id === processId ? updatedProc : selectedProcess,
      });
      await autoSave(
        { ...updatedProject, analysis_results: analysisResults },
        'Process updated & saved',
        'Process updated (save failed)',
      );
    },

    // Delete a process and strip every connector that pointed at it.
    deleteProcess: async (processId) => {
      const { project, selectedProcess, analysisResults } = get();
      if (!project || !project.processes[processId]) return;
      const proc = project.processes[processId];
      const confirmed = await modal().showConfirm(
        `Delete "${(proc.name || processId).replace(/_/g, ' ')}"? This removes the step and any connections to it.`,
        { title: 'Delete Process', type: 'warning', confirmText: 'Delete', cancelText: 'Cancel' },
      );
      if (!confirmed) return;
      const processes = {};
      Object.values(project.processes).forEach((p) => {
        if (p.id === processId) return;
        processes[p.id] = { ...p, next_process_ids: (p.next_process_ids || []).filter((id) => id !== processId) };
      });
      const updatedProject = { ...project, processes };
      set({
        project: updatedProject,
        dirty: true,
        selectedProcess: selectedProcess?.id === processId ? null : selectedProcess,
      });
      await autoSave(
        { ...updatedProject, analysis_results: analysisResults },
        'Process deleted & saved',
        'Process deleted (save failed)',
      );
    },

    // Rename a process from the inspector header.
    renameProcess: async (processId, newName) => {
      const { project, selectedProcess } = get();
      if (!project || !newName?.trim()) return;
      const cleanName = newName.trim().replace(/\s+/g, '_');
      const updatedProc = { ...project.processes[processId], name: cleanName };
      const updatedProject = { ...project, processes: { ...project.processes, [processId]: updatedProc } };
      await get().updateProjectTable(updatedProject);
      if (selectedProcess?.id === processId) set({ selectedProcess: updatedProc });
    },

    // ---- analysis ------------------------------------------------------------
    runLCA: async () => {
      const { project } = get();
      if (!project) return;
      // No TRACI scores — AutoLinkPanel in the Results tab guides DB linking.
      if (!isTemplateMode(project)) return;
      try {
        set({ isAnalyzing: true, statusMessage: 'Running LCA analysis…' });
        const resolvedSubs = await resolveAllSubInventories(project);
        const lcaResults = runTemplateLCA(project, resolvedSubs);
        if (!lcaResults.success) throw new Error(lcaResults.error);
        const updated = { ...get().analysisResults, lca: lcaResults };
        set({ analysisResults: updated, dirty: false });
        await autoSave(
          { ...project, analysis_results: updated },
          'LCA analysis completed & saved',
          'LCA analysis completed (save failed)',
        );
      } catch (err) {
        console.error('[store] LCA failed:', err);
        setStatus(`LCA failed: ${err.message}`);
        modal().showAlert(`LCA analysis failed:\n${err.message}`, { title: 'LCA Error', type: 'error' });
      } finally {
        set({ isAnalyzing: false });
      }
    },

    runTEA: async () => {
      const { project } = get();
      if (!project) return;
      try {
        set({ isAnalyzing: true, statusMessage: 'Running TEA analysis…' });
        const resolvedSubs = await resolveAllSubInventories(project);
        const teaResults = runTemplateTEA(project, resolvedSubs);
        if (!teaResults.success) throw new Error(teaResults.error);
        const updated = { ...get().analysisResults, tea: teaResults };
        set({ analysisResults: updated, dirty: false });
        await autoSave(
          { ...project, analysis_results: updated },
          'TEA analysis completed & saved',
          'TEA analysis completed (save failed)',
        );
      } catch (err) {
        console.error('[store] TEA failed:', err);
        setStatus(`TEA failed: ${err.message}`);
        modal().showAlert(`TEA analysis failed:\n${err.message}`, { title: 'TEA Error', type: 'error' });
      } finally {
        set({ isAnalyzing: false });
      }
    },

    runCircularity: async () => {
      const { project } = get();
      if (!project) return;
      try {
        set({ isAnalyzing: true, statusMessage: 'Running circularity analysis…' });
        const circResults = runCircularity(project);
        const updated = { ...get().analysisResults, circularity: circResults };
        set({ analysisResults: updated, dirty: false });
        await autoSave(
          { ...project, analysis_results: updated },
          'Circularity analysis completed & saved',
          'Circularity analysis completed (save failed)',
        );
      } catch (err) {
        console.error('[store] Circularity failed:', err);
        setStatus(`Circularity failed: ${err.message}`);
      } finally {
        set({ isAnalyzing: false });
      }
    },

    runAll: async () => {
      const { project } = get();
      if (!project) return;
      try {
        set({ isAnalyzing: true, statusMessage: 'Running all analyses…' });
        const resolvedSubs = await resolveAllSubInventories(project);
        const lca = runTemplateLCA(project, resolvedSubs);
        const tea = runTemplateTEA(project, resolvedSubs);
        const circ = runCircularity(project);
        set({ analysisResults: { lca, tea, circularity: circ }, dirty: false });
        await autoSave(
          { ...project, analysis_results: { lca, tea, circularity: circ } },
          'All analyses completed & saved',
          'All analyses completed (save failed)',
        );
      } catch (err) {
        console.error('[store] Analysis failed:', err);
        setStatus('Analysis failed');
      } finally {
        set({ isAnalyzing: false });
      }
    },

    // ---- export --------------------------------------------------------------
    exportResults: () => {
      const { project, analysisResults } = get();
      if (!project) return;
      try {
        const wb = XLSX.utils.book_new();
        const dateStr = new Date().toISOString().split('T')[0];
        const projectName = (project.project_info?.name || 'Untitled').replace(/\s+/g, '_');

        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildLCAExportRows(project, analysisResults)), 'LCA');
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildTEAExportRows(project, analysisResults)), 'TEA');

        const fileName = `${projectName}_Results_${dateStr}.xlsx`;
        XLSX.writeFile(wb, fileName);
        setStatus(`Exported: ${fileName}`);
      } catch (err) {
        console.error('[store] Export failed:', err);
        setStatus('Export failed');
        modal().showAlert(`Export failed:\n${err.message}`, { title: 'Export Error', type: 'error' });
      }
    },

    // ---- save as template ----------------------------------------------------
    overwriteTemplate: async () => {
      const { project } = get();
      const { showAlert } = modal();
      if (!project) return;
      const templateId = sanitizeFileName(project.template_id || project.project_name || 'custom_template');
      const fileName = templateId.endsWith('.json') ? templateId : `${templateId}.json`;
      // A saved copy under a built-in's file name would replace that template
      // for every later project, so built-ins can only be saved under a new name.
      if (isBuiltinTemplateFile(fileName)) {
        set({ showTemplateSaveDialog: true, newTemplateName: '' });
        showAlert(
          `"${fileName}" is a built-in template and can't be overwritten.\n\nUse "Save as New Template" to keep your changes under a new name.`,
          { title: 'Built-in Template', type: 'info' },
        );
        return;
      }
      try {
        const result = await ipc.invoke('save-user-template', fileName, buildTemplateData(project, templateId));
        if (result?.success) {
          setStatus(`Template saved: ${fileName}`);
          showAlert(
            `Template saved as "${fileName}".\n\nOther projects referencing this template (e.g. NMC + Coating) will automatically use your customized data.`,
            { title: 'Template Saved', type: 'success' },
          );
        }
      } catch (err) {
        console.error('[store] Save template failed:', err);
        showAlert(`Failed to save template:\n${err.message}`, { title: 'Template Error', type: 'error' });
      }
    },

    saveNewTemplate: async () => {
      const { project, newTemplateName } = get();
      const { showAlert } = modal();
      if (!project || !newTemplateName.trim()) return;
      const templateId = sanitizeFileName(newTemplateName.trim());
      const fileName = `${templateId}.json`;
      if (isBuiltinTemplateFile(fileName)) {
        showAlert(
          `"${newTemplateName.trim()}" is the name of a built-in template. Choose a different name.`,
          { title: 'Name Taken', type: 'warning' },
        );
        return;
      }
      try {
        const result = await ipc.invoke('save-user-template', fileName, buildTemplateData(project, templateId));
        if (result?.success) {
          set({ showTemplateSaveDialog: false, newTemplateName: '', statusMessage: `Template saved: ${fileName}` });
          showAlert(
            `New template saved as "${fileName}".\n\nYou can reference this template in other projects.`,
            { title: 'Template Saved', type: 'success' },
          );
        }
      } catch (err) {
        console.error('[store] Save template failed:', err);
        showAlert(`Failed to save template:\n${err.message}`, { title: 'Template Error', type: 'error' });
      }
    },

    // ---- compare -------------------------------------------------------------
    saveComparison: async (compData) => {
      const { project, analysisResults } = get();
      const updated = { ...project, comparison: compData };
      set({ project: updated });
      await autoSave(
        { ...updated, analysis_results: analysisResults },
        'Comparison saved to project',
        'Comparison saved (file save failed)',
      );
    },

    // ---- agent: undo / startup reset / scaling -------------------------------
    // Return to the startup screen without the save-confirm prompt (used by the
    // walkthrough, whose demo project is throwaway).
    goToStartup: () => {
      set({ showStartup: true, project: null, selectedProcess: null, analysisResults: emptyResults(), statusMessage: 'Ready' });
      return { ok: true };
    },

    // Copilot writes are snapshotted before they apply (Undo bar in the panel).
    snapshotForUndo: ({ label } = {}) => {
      const { project, analysisResults } = get();
      set((s) => ({ agentHistory: [...s.agentHistory.slice(-49), { project, analysisResults, label }] }));
      return { ok: true };
    },

    undo: () => {
      const { agentHistory, selectedProcess } = get();
      if (!agentHistory || agentHistory.length === 0) return { ok: false, label: null };
      const last = agentHistory[agentHistory.length - 1];
      const restoredSelected = selectedProcess && last.project?.processes
        ? last.project.processes[selectedProcess.id] || null
        : selectedProcess;
      set({
        project: last.project,
        analysisResults: last.analysisResults || emptyResults(),
        selectedProcess: restoredSelected,
        agentHistory: agentHistory.slice(0, -1),
      });
      if (last.project) {
        autoSave(
          { ...last.project, analysis_results: last.analysisResults || emptyResults() },
          'Reverted last change',
          'Reverted (save failed)',
        );
      }
      return { ok: true, label: last.label || null };
    },

    setScalingFactor: (value) => {
      const { project } = get();
      if (!project) return { error: 'No project loaded' };
      const updated = {
        ...project,
        project_info: { ...project.project_info, scaling_factor: Number(value) || 1.0 },
      };
      get().updateProjectTable(updated);
      return { scaling_factor: updated.project_info.scaling_factor };
    },

    // Functional unit basis: 'energy' (per kWh) or 'mass' (per kg).
    setFuBasis: (basis) => {
      const { project } = get();
      if (!project) return { error: 'No project loaded' };
      const b = basis === 'energy' ? 'energy' : 'mass';
      const updated = {
        ...project,
        project_info: { ...project.project_info, fu_basis: b },
      };
      get().updateProjectTable(updated);
      return { fu_basis: b };
    },

    // Energy density (Wh/kg) used to convert the mass FU to a per-kWh basis.
    setEnergyDensity: (value) => {
      const { project } = get();
      if (!project) return { error: 'No project loaded' };
      const v = value === null || value === '' ? null : (Number(value) || null);
      const updated = {
        ...project,
        project_info: { ...project.project_info, energy_density_wh_per_kg: v },
      };
      get().updateProjectTable(updated);
      return { energy_density_wh_per_kg: v };
    },
  };
});

// =============================================================================
// Export-row builders — pure helpers kept alongside exportResults().
// =============================================================================

function buildLCAExportRows(project, results) {
  const rows = [];
  const lca = results?.lca;
  const name = project.project_info?.name || 'Untitled';

  rows.push(['LiTeCAB — LCA Results']);
  rows.push(['Project', name]);
  rows.push(['Chemistry', project.project_info?.chemistry || '']);
  rows.push(['Method', lca?.method || 'TRACI v2.1']);
  rows.push(['Mode', lca?.mode || 'template']);
  rows.push(['Scaling Factor', lca?.scaling_factor || 1]);
  rows.push(['Functional Unit Basis', lca?.functional_unit_basis === 'energy' ? 'per kWh' : 'per kg']);
  if (lca?.functional_unit_basis === 'energy') {
    rows.push(['Energy Density (Wh/kg)', lca?.energy_density_wh_per_kg ?? '']);
    rows.push(['Energy per FU (kWh)', lca?.energy_kwh ?? '']);
  }
  rows.push(['Export Date', new Date().toLocaleString()]);
  rows.push([]);

  const methodResults = lca?.results || {};
  if (Object.keys(methodResults).length > 0) {
    rows.push(['Impact Category', 'Total', 'Unit']);
    for (const [key, data] of Object.entries(methodResults)) {
      rows.push([key, data.total, data.unit]);
    }
    rows.push([]);

    // Process breakdown for each category
    for (const [key, data] of Object.entries(methodResults)) {
      const pb = data.process_breakdown || {};
      if (Object.keys(pb).length === 0) continue;

      rows.push([`Breakdown: ${key} (${data.unit})`]);
      rows.push(['Process', 'Impact', 'Input Count']);
      for (const [, procData] of Object.entries(pb)) {
        rows.push([
          procData.process_name,
          procData.total,
          (procData.inputs || []).length,
        ]);
      }
      rows.push([]);
    }
  }

  return rows;
}

function buildTEAExportRows(project, results) {
  const rows = [];
  const tea = results?.tea;
  const name = project.project_info?.name || 'Untitled';

  rows.push(['LiTeCAB — TEA Results']);
  rows.push(['Project', name]);
  rows.push(['Chemistry', project.project_info?.chemistry || '']);
  rows.push(['Mode', tea?.mode || 'template']);
  rows.push(['Scaling Factor', tea?.scaling_factor || 1]);
  rows.push(['Functional Unit Basis', tea?.functional_unit_basis === 'energy' ? 'per kWh' : 'per kg']);
  if (tea?.functional_unit_basis === 'energy') {
    rows.push(['Energy Density (Wh/kg)', tea?.energy_density_wh_per_kg ?? '']);
    rows.push(['Energy per FU (kWh)', tea?.energy_kwh ?? '']);
  }
  rows.push(['Export Date', new Date().toLocaleString()]);
  rows.push([]);

  if (tea) {
    rows.push(['Total Cost', tea.total_cost, tea.cost_unit || 'USD']);
    rows.push(['Note', tea.note || '']);
    rows.push([]);

    const pb = tea.process_breakdown || {};
    if (Object.keys(pb).length > 0) {
      rows.push(['Process Cost Breakdown']);
      rows.push(['Process', `Total Cost (${tea.cost_unit || 'USD'})`, 'Items']);
      for (const [, procData] of Object.entries(pb)) {
        rows.push([procData.process_name, procData.total_cost, (procData.items || []).length]);
      }
      rows.push([]);

      // Item-level detail
      rows.push(['Item-Level Detail']);
      rows.push(['Process', 'Item', 'Qty', 'Unit', 'Cost/Unit', 'Total Cost']);
      for (const [, procData] of Object.entries(pb)) {
        for (const item of (procData.items || [])) {
          rows.push([
            procData.process_name,
            item.name,
            item.quantity,
            item.unit,
            item.cost_per_unit,
            item.total_cost,
          ]);
        }
      }
    }
  }

  return rows;
}

export default useProjectStore;
